<template>
    <div>
        <h4 class="quantity-adjuster-title">
            {{ $t('myaccount.quick.add.quantity.adjuster.title') }}
        </h4>

        <ds-card
            class="quantity-adjuster-card"
            data-qa="quantity-adjuster-card"
        >
            <ds-card-header
                data-qa="quantity-adjuster-sub-title"
                class="quantity-adjuster-sub-title"
            >
                <h4 class="quantity-adjuster-header">
                    {{ $t('myaccount.quick.add.quantity.adjuster.subtitle') }}
                </h4>
                <hr />
            </ds-card-header>
            <ds-card-body class="card-body">
                <div class="button-controls">
                    <DsIconButton
                        v-if="isMinusButtonDisabled"
                        class="disabled-button"
                        data-qa="disabled-minus-button"
                        disabled
                        name="minus-circle"
                    />
                    <DsIconButton
                        v-else
                        data-qa="enabled-minus-button"
                        name="minus-circle"
                        @click="decreaseSelectedTier"
                    />

                    <DsIconButton
                        v-if="isPlusButtonDisabled"
                        class="disabled-button"
                        data-qa="disabled-plus-button"
                        disabled
                        name="add-circle"
                    />
                    <DsIconButton
                        v-else
                        data-qa="enabled-plus-button"
                        name="add-circle"
                        @click="increaseSelectedTier"
                    />
                </div>


                <div class="quantity-and-price">
                    <div
                        class="total-count"
                        data-qa="total-count"
                    >
                        <p class="currently-selected-quantity">
                            {{ getDisplayQuantitySentence() }}
                        </p>
                        <p class="currently-selected-quantity-footer">
                            {{ getSelectedQuantityFooter() }}
                        </p>
                    </div>

                    <div class="currently-selected-price">
                        <p
                            class="price"
                            data-qa="price"
                        >
                            {{ getPrice() }}
                        </p>
                        <DsStatus
                            v-if="isCurrentTierGreaterOrEqualsSelectedTier"
                            emphasis="high"
                        >
                            {{ $t('myaccount.quick.add.current.plan') }}
                        </DsStatus>
                    </div>
                </div>
            </ds-card-body>
        </ds-card>

        <div
            class="progress-bar"
            data-qa="progress-bar"
        >
            <div class="gauge">
                <span>0</span>
                <span class="number-of-contact-limit">
                    {{ numberWithCommas(progressBarMax) }}
                </span>
            </div>
            <ds-progress-bar
                v-if="contactUsedPercent<1"
                :progress="contactDetails.contactUsed"
                :max="progressBarMax"
                :label="progressBarLabel"
                hide-label-value
            />
            <ds-progress-bar
                v-else
                class="total-contact-progress-bar-over-full"
                :progress="progressBarMax"
                :max="contactDetails.contactUsed"
                :label="progressBarLabel"
                hide-label-value
            />
        </div>
    </div>
</template>

<script>
const MONTH = 'MONTH';

export default {
    props: {
        currentTier: Number,
        /**
         * Contact specific data. Should contain the following fields:
         * contactUsed: number of contacts used
         * contactLimit: limit on the number of contacts
         * contactPricingTiers: pricing tiers specific to contacts addon
         * changeEstimate: estimate price for the change
         */
        contactDetails: Object,
        /**
         * Subscription specific data. Should contain the following fields:
         * differences: differences between current subscription state and future subscription state
         * nextBillingAt: next billing date for the subscription
         * billingFrequency: monthly/yearly billing
         * currencySymbol: symbol for the currency
         * scheduledChangesEstimateTotal: estimated total for the future state of the subscription
         * showScheduledChangesNotice: controls whether scheduled changes notice should be rendered
         * or not.
         * lineItems: addons on the subscription
         * estimate: estimate current pricing
         */
        subscriptionDetails: Object,
        parentSelectedTier: Number,
    },

    data() {
        return {
            localSelectedTier: -1,
        };
    },

    async created() {
        this.localSelectedTier = this.parentSelectedTier;
    },

    computed: {
        contactUsedPercent() {
            return this.contactDetails?.contactUsed / this.progressBarMax;
        },

        isCurrentTierGreaterOrEqualsSelectedTier() {
            return this.currentTier >= this.localSelectedTier;
        },

        isMinusButtonDisabled() {
            return this.isCurrentTierGreaterOrEqualsSelectedTier;
        },

        isPlusButtonDisabled() {
            return this.localSelectedTier === this.contactDetails?.contactPricingTiers.length - 2;
        },

        progressBarMax() {
            let additionalContacts = 0;

            if (this.localSelectedTier > this.currentTier) {
                additionalContacts = this.contactDetails?.contactPricingTiers.at(this.localSelectedTier)?.maxUnits - this.contactDetails?.contactPricingTiers.at(this.currentTier)?.maxUnits;
            }

            return this.contactDetails?.contactLimit + additionalContacts;
        },

        progressBarLabel() {
            return this.$t('myaccount.quick.add.progress.bar.contacts.text', { contactsUsed: this.numberWithCommas(this.contactDetails?.contactUsed), contactsLimit: this.numberWithCommas(this.progressBarMax) });
        },

        contactLimit() {
            return this.contactDetails?.contactLimit;
        },
    },

    methods: {
        getSelectedQuantityFooter() {
            if (this.localSelectedTier < this.currentTier) {
                this.setSelectedTierToCurrentTier();
            }

            const additionalQuantityCurrent = this.contactDetails?.contactPricingTiers.at(this.currentTier)?.maxUnits;
            const additionalQuantitySelected = this.contactDetails?.contactPricingTiers.at(this.localSelectedTier)?.maxUnits;

            const totalQuantity = this.localSelectedTier > this.currentTier ? ((additionalQuantitySelected - additionalQuantityCurrent) + this.contactLimit) : this.contactLimit;

            return this.$t('myaccount.quick.add.selected.quantity.footed.contacts.with.change', { quantity: this.numberWithCommas(totalQuantity) });
        },

        getDisplayQuantitySentence() {
            return this.$t('myaccount.quick.add.selected.quantity.additional.contacts', { quantity: this.getDisplayQuantity() });
        },

        getDisplayQuantity() {
            if (this.localSelectedTier < this.currentTier) {
                this.setSelectedTierToCurrentTier();
            }

            if (this.currentTier < this.localSelectedTier) {
                return this.numberWithCommas(this.contactDetails?.contactPricingTiers.at(this.localSelectedTier)?.maxUnits);
            }

            return this.numberWithCommas(this.contactDetails?.contactPricingTiers.at(this.currentTier)?.maxUnits);
        },

        getPrice() {
            if (this.localSelectedTier < this.currentTier) {
                this.setSelectedTierToCurrentTier();
            }

            let price;

            if (this.currentTier === -1 && this.localSelectedTier === -1) {
                price = 0;
            } else if (this.currentTier >= this.localSelectedTier) {
                price = this.contactDetails?.contactPricingTiers.at(this.currentTier)?.unitPrice;
            } else {
                price = this.contactDetails?.contactPricingTiers.at(this.localSelectedTier)?.unitPrice;
            }

            const endingText = this.$t(this.subscriptionDetails?.billingFrequency === MONTH ? 'myaccount.quick.add.monthlyEnding' : 'myaccount.quick.add.yearlyEnding');

            return `${this.subscriptionDetails?.currencySymbol}${this.numberWithCommas(price)}${endingText}`;
        },

        numberWithCommas(x) {
            if (!x) {
                return 0;
            }
            const parts = x.toString().split('.');

            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

            return parts.join('.');
        },

        increaseSelectedTier() {
            if (this.localSelectedTier < this.contactDetails?.contactPricingTiers.length - 2) {
                this.localSelectedTier += 1;
                this.$emit('selected-tier-change', this.localSelectedTier);
            }
        },

        decreaseSelectedTier() {
            if (this.localSelectedTier > this.currentTier) {
                this.localSelectedTier -= 1;
                this.$emit('selected-tier-change', this.localSelectedTier);
            }
        },

        setSelectedTierToCurrentTier() {
            this.localSelectedTier = this.currentTier;
            this.$emit('selected-tier-change', this.localSelectedTier);
        },
    },
};
</script>

<style lang="scss" scoped>
.quantity-adjuster-title {
    font-size: $font-size-xl;
    font-weight: $font-weight-bold;
    line-height: $line-height-xl;
    padding-bottom: $spacing-100;
}

.quantity-adjuster-card {
    min-width: 327px;

    .quantity-adjuster-header {
        color: $color-gray-800;
        line-height: $line-height-lg;
        padding: $spacing-200;
        font-weight: $font-weight-regular;
        font-size: 1.125rem;
    }
    hr {
        margin: 0 $spacing-200;
    }

    .card-body {
        display: flex;
        justify-content: space-between;
    }

    .quantity-and-price {
        display: flex;
        width: 100%;
    }

    .total-count {
        display: inline-block;
        margin-right: auto;
        padding: $spacing-050;
    }

    .currently-selected-quantity {
        font-weight: 500;
        font-size: $font-size-xl;
        line-height: $line-height-xl;
        margin: 0;
    }

    .currently-selected-quantity-footer {
        font-weight: $font-weight-regular;
        font-size: $font-size-xs;
        line-height: $line-height-sm;
        color: $color-gray-800;
        margin: 0;
    }

    .currently-selected-price {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .price {
        font-weight: 500;
        font-size: $font-size-xl;
        line-height: $line-height-xl;
        float: right;
        padding: $spacing-050 $spacing-050 0;
        margin: 0;
    }

    .button-controls {
        display: flex;
    }

    button {
        display: block;
        max-height: 40px;
    }

    button.icon-only.disabled-button {
        --button-text-color: #{$color-ink-700};
    }
}

.progress-bar {
    min-width: 327px;
    padding: 0 $spacing-600 $spacing-300 $spacing-600;

    .total-contact-progress-bar-over-full {
        --progress-bar-color: #{$color-blue-700};

        &::v-deep .bar-container {
            background-color: $color-blue-900;
        }
    }

    .gauge {
        font-size: $font-size-xs;
        padding: $spacing-300 $spacing-600 $spacing-100 $spacing-600;

        .number-of-contact-limit {
            float: right;
        }
    }
}

@media screen and ($small) {
    .quantity-adjuster-card {
        .button-controls {
            flex-direction: column;
        }
    }

    .progress-bar {
        padding: 0;

        .gauge {
            padding: $spacing-300 0 $spacing-100 0;
        }
    }

    .quantity-and-price {
        .price {
            margin: 0;
        }
    }
}
</style>
